// useMarketTracks
// -----------------------------------------------------------------------------
// Track-first data layer for the v4 dashboard. Owns:
//   - GET /api/tracks (registry + curation summary + briefings)
//   - GET /api/tracks/history (per-track weekly history maps)
//   - GET /api/health (the existing freshness pill)
//
// UI state (selectedTrackId, showExploratory, etc.) stays in App.js.
// -----------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    normalizeTracks,
    filterBoardTracks,
    sortTracksDefault,
    subPatternsFor,
    relatedTracksFor,
    sortHistoryAsc,
    deriveCurationSummary,
} from "../lib/tracks";
import {
    computeMovementCards,
    briefingReferencedItems,
} from "../lib/tracks/movement";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// Map a v4 weeklyBriefing row (Briefing*-prefixed) to the legacy briefing
// shape consumed by ExecutiveMarketRead. v2/v3 rows pass through unchanged.
const adaptBriefing = (b) => {
    if (!b || typeof b !== "object") return b;
    const v4 = "BriefingHeadline" in b || "BriefingHeadlineShort" in b;
    if (!v4) return b;
    return {
        yearWeek: b.YearWeek || b.yearWeek || null,
        week: b.Week || b.BriefingWeek || b.SignalWeek || b.week || null,
        dateRange: b.BriefingDateRange || b.dateRange || null,
        headline: b.BriefingHeadline || b.headline || null,
        headlineShort: b.BriefingHeadlineShort || b.headlineShort || null,
        bigPicture: b.BriefingBigPicture || b.bigPicture || null,
        bigPictureShort: b.BriefingBigPictureShort || b.bigPictureShort || null,
        executiveSummary: b.BriefingExecutiveSummary || b.executiveSummary || null,
        keyTakeaways: Array.isArray(b.BriefingKeyTakeawaysJSON) ? b.BriefingKeyTakeawaysJSON : (b.keyTakeaways || []),
        marketStructureImplications: Array.isArray(b.BriefingMarketStructureImplicationsJSON) ? b.BriefingMarketStructureImplicationsJSON : (b.marketStructureImplications || []),
        watchItems: Array.isArray(b.BriefingWatchItemsJSON) ? b.BriefingWatchItemsJSON : (b.watchItems || []),
        topSignals: Array.isArray(b.TopSignalsJSON) ? b.TopSignalsJSON : (b.topSignals || []),
        regulatoryWatch: Array.isArray(b.RegulatoryWatchJSON) ? b.RegulatoryWatchJSON : (b.regulatoryWatch || []),
        topSignalsCount: b.TopSignalsCount,
        regulatoryWatchCount: b.RegulatoryWatchCount,
        createdAt: b.CreatedAt || b.createdAt || null,
        // Structured evidence fields (signals-history v5+). Always passed
        // through verbatim — `EvidenceChips` handles missing/empty.
        evidenceLayer: b.evidenceLayer || null,
        bigPictureEvidence: b.bigPictureEvidence || null,
        executiveSummaryEvidence: b.executiveSummaryEvidence || null,
        keyTakeawaysStructured: Array.isArray(b.keyTakeawaysStructured) ? b.keyTakeawaysStructured : null,
        marketStructureImplicationsStructured: Array.isArray(b.marketStructureImplicationsStructured) ? b.marketStructureImplicationsStructured : null,
        watchItemsStructured: Array.isArray(b.watchItemsStructured) ? b.watchItemsStructured : null,
    };
};

// Build a `{TrackID -> track}` lookup from the `marketTracks[]` array. Used
// when the upstream JSON no longer ships `marketTracksById` (the pre-built
// index is now derived client-side so n8n can drop it from the payload).
const buildTracksById = (list) => {
    const out = {};
    for (const t of list || []) {
        const id = t && t.TrackID;
        if (id) out[id] = t;
    }
    return out;
};

const useMarketTracks = ({ selectedTrackId, showExploratory }) => {
    const [tracks, setTracks] = useState([]);
    const [tracksById, setTracksById] = useState({});
    const [historyByTrackId, setHistoryByTrackId] = useState({});
    const [signals, setSignals] = useState([]);
    const [briefings, setBriefings] = useState([]);
    const [curationSummary, setCurationSummary] = useState(null);
    const [latestIndexedWeek, setLatestIndexedWeek] = useState(null);
    const [trackCount, setTrackCount] = useState(0);
    const [historyCount, setHistoryCount] = useState(0);
    const [briefingCount, setBriefingCount] = useState(0);
    const [schemaVersion, setSchemaVersion] = useState(null);
    // v5 governed-signal-architecture (feature-detected; null on legacy JSONs)
    const [marketTrackStructure, setMarketTrackStructure] = useState(null);
    const [marketViewUiHints, setMarketViewUiHints] = useState(null);
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchHealth = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/health`);
            setHealth(r.data);
        } catch {
            // keep prior health if fetch fails
        }
    }, []);

    useEffect(() => {
        fetchHealth();
        const id = setInterval(fetchHealth, 60000);
        return () => clearInterval(id);
    }, [fetchHealth]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const [tr, th, sh] = await Promise.all([
                    axios.get(`${API}/tracks`),
                    axios.get(`${API}/tracks/history`),
                    axios.get(`${API}/signals/history`),
                ]);
                if (cancelled) return;
                const trd = tr.data || {};
                const thd = th.data || {};
                const shd = sh.data || {};
                // Defensive publish-safety: drop any non-publishable
                // track before it enters component state. `normalizeTracks`
                // also re-applies the same filter so derived lookups
                // (`tracksById`, `historyByTrackId`) stay in sync.
                const publishableTracks = normalizeTracks(trd.tracks || []);
                const publishableIds = new Set(publishableTracks.map((t) => t.TrackID));
                setTracks(publishableTracks);
                // Derive `tracksById` from the `tracks[]` array when the upstream
                // doesn't ship a pre-built index. Honour the server-provided
                // index when it's non-empty (back-compat) — but still gate
                // it by the publishable set.
                const serverTracksById = trd.tracksById && Object.keys(trd.tracksById).length ? trd.tracksById : null;
                const rawTracksById = serverTracksById || buildTracksById(trd.tracks || []);
                const safeTracksById = {};
                for (const [id, t] of Object.entries(rawTracksById || {})) {
                    if (publishableIds.has(id)) safeTracksById[id] = t;
                }
                setTracksById(safeTracksById);
                const rawHistory = thd.byTrackId || {};
                const safeHistory = {};
                for (const [id, weeks] of Object.entries(rawHistory)) {
                    if (publishableIds.has(id)) safeHistory[id] = weeks;
                }
                setHistoryByTrackId(safeHistory);
                setSignals(Array.isArray(shd.signals) ? shd.signals : []);
                setBriefings(Array.isArray(trd.weeklyBriefings) ? trd.weeklyBriefings : []);
                setCurationSummary(trd.trackCurationSummary || null);
                setLatestIndexedWeek(trd.latestIndexedWeek || null);
                setTrackCount(typeof trd.trackCount === "number" ? trd.trackCount : (trd.tracks || []).length);
                setHistoryCount(typeof thd.count === "number" ? thd.count : 0);
                setBriefingCount(typeof trd.briefingCount === "number" ? trd.briefingCount : 0);
                setSchemaVersion(trd.schemaVersion || thd.schemaVersion || null);
                // v5 governed-signal-architecture (null on legacy JSONs)
                setMarketTrackStructure(trd.marketTrackStructure || null);
                setMarketViewUiHints(trd.marketViewUiHints || null);
            } catch (e) {
                if (!cancelled) setError(e.message);
            }
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; };
    }, []);

    // Latest briefing — we no longer have a global week selector, so the
    // Executive Market Read defaults to the latest indexed week's briefing.
    // v4 wrapper renamed every briefing key (Briefing* + YearWeek). We map
    // them to the legacy shape that ExecutiveMarketRead already understands.
    const latestBriefing = useMemo(() => {
        if (!briefings.length) return null;
        const yw = latestIndexedWeek && latestIndexedWeek.yearWeek;
        const sw = latestIndexedWeek && latestIndexedWeek.week;
        const ywOf = (b) => b.yearWeek || b.YearWeek || null;
        const swOf = (b) => b.week || b.Week || b.BriefingWeek || b.SignalWeek || null;
        let raw = null;
        if (yw) raw = briefings.find((b) => b && ywOf(b) === yw) || null;
        if (!raw && sw) raw = briefings.find((b) => b && swOf(b) === sw) || null;
        if (!raw) {
            const sorted = [...briefings].sort((a, b) => (ywOf(b) || "").localeCompare(ywOf(a) || ""));
            raw = sorted[0] || null;
        }
        return raw ? adaptBriefing(raw) : null;
    }, [briefings, latestIndexedWeek]);

    const visibleBoardTracks = useMemo(
        () => sortTracksDefault(filterBoardTracks(tracks, { showExploratory })),
        [tracks, showExploratory]
    );

    const summary = useMemo(
        () => curationSummary || deriveCurationSummary(tracks),
        [curationSummary, tracks]
    );

    const selectedTrack = useMemo(() => {
        if (!selectedTrackId) return null;
        return tracks.find((t) => t.TrackID === selectedTrackId) || null;
    }, [selectedTrackId, tracks]);

    const selectedTrackHistory = useMemo(() => {
        if (!selectedTrack) return [];
        const list = historyByTrackId[selectedTrack.TrackID] || [];
        return sortHistoryAsc(list);
    }, [selectedTrack, historyByTrackId]);

    const subPatterns = useMemo(
        () => (selectedTrack ? subPatternsFor(selectedTrack, tracks) : []),
        [selectedTrack, tracks]
    );

    const relatedTracks = useMemo(
        () => (selectedTrack ? relatedTracksFor(selectedTrack, tracks) : []),
        [selectedTrack, tracks]
    );

    // Latest-week signal rows (used for "What moved this week" + Latest movement
    // block in the Track Detail Panel).
    const latestYearWeek = latestIndexedWeek && latestIndexedWeek.yearWeek;
    const latestSignals = useMemo(() => {
        if (!latestYearWeek) return [];
        return signals.filter((s) => (s.yearWeek || s.YearWeek) === latestYearWeek);
    }, [signals, latestYearWeek]);

    // Maps for efficient lookup from inside the detail panel + briefing chips.
    const latestSignalByTrackId = useMemo(() => {
        const m = new Map();
        for (const s of latestSignals) {
            const tid = s.trackID || s.TrackID;
            if (tid) m.set(tid, s);
        }
        return m;
    }, [latestSignals]);

    const latestSignalByPipeId = useMemo(() => {
        const m = new Map();
        for (const s of latestSignals) {
            const sid = s.signalID || s.SignalID;
            if (sid) m.set(sid, s);
        }
        return m;
    }, [latestSignals]);

    const movementCards = useMemo(() => computeMovementCards(latestSignals), [latestSignals]);

    const briefingReferences = useMemo(
        () => briefingReferencedItems(latestBriefing, latestSignalByPipeId, tracksById, historyByTrackId),
        [latestBriefing, latestSignalByPipeId, tracksById, historyByTrackId]
    );

    const briefingReferencedTrackIds = useMemo(() => {
        const s = new Set();
        for (const r of briefingReferences) {
            if (r.trackId) s.add(r.trackId);
        }
        return s;
    }, [briefingReferences]);

    return {
        // raw / fetched
        tracks,
        tracksById,
        historyByTrackId,
        briefings,
        curationSummary: summary,
        latestIndexedWeek,
        trackCount,
        historyCount,
        briefingCount,
        schemaVersion,
        // v5 governed-signal-architecture (null on legacy JSONs — feature-detect downstream)
        marketTrackStructure,
        marketViewUiHints,
        health,
        loading,
        error,
        // derived
        visibleBoardTracks,
        selectedTrack,
        selectedTrackHistory,
        subPatterns,
        relatedTracks,
        latestBriefing,
        // v0.5 — weekly movement + briefing cross-links
        signals,
        latestSignals,
        latestSignalByTrackId,
        latestSignalByPipeId,
        movementCards,
        briefingReferences,
        briefingReferencedTrackIds,
    };
};

export default useMarketTracks;
